import {
  UpdateEventListener,
  FireableUpdateEventListener
} from 'utils/UpdateEventListener';
import { FormikProps } from 'formik';
import { validateEmpty, validateUrl } from 'utils/ValidateUtils';
import _ from 'lodash';
import {
  CreativeFormBasicData,
  FormContentModel
} from './FormContent/FormContentModel';
import { CreativeManager } from 'core/creative/CreativeManager';
import i18n from 'i18next';
import { CreativeType, isNative } from 'core/creative/Creative';
import { UrlMacroInstruction } from './FormContent/UrlMacroInstruction';
import { CompositeSettingOption } from './FormContent/CompositeSettingOption';
import {
  getCompositeSettingInvalidHint,
  isCompositeSettingBlocked
} from 'core/channelGroup/compositeSettingGate';
import styles from './creativeBasicForm.module.scss';
import { FormConfig } from 'components/common/form/FormConfig';
import { renderEndTime, renderStartTime } from './CreativeDayRangeRenderer';

export interface CreativeBasicFormModel {
  readonly type: string;
  readonly initCreative: CreativeFormBasicData;
  readonly state: CreativeBasicFormState;
  readonly event: UpdateEventListener<CreativeBasicFormModel>;
  readonly tenmaxCategories: Array<SelectOptions>;
  readonly enabledCreativeTypes: number[];
  readonly needLandingUrl: boolean;
  setFormikProps(props: FormikProps<any>): void;
  validate(): any;
  getCreativeBasicValue(): any;
  getContentModelByCreativeType(): FormContentModel | undefined;
  onCreativeTypeChange(newType): void;
  onUnmount(handler?: number): void;
  setLimitationHook(
    hook: (
      operate: string,
      limitationType: string,
      label: string,
      value: string
    ) => void
  ): void;
  setShowStartTime(showStartTime: boolean): void;
  setShowEndTime(showEndTime: boolean): void;
  setHintModalData(data: { component: any; props: any }): void;
  triggerUrlMacroHint(): void;
  getInitFormConfig(): FormConfig;
}

export type CreativeBasicFormProps = {
  readonly model: CreativeBasicFormModel;
};

export type CreativeBasicFormState = {
  readonly loading: boolean;
  readonly needLandingUrl: boolean;
  readonly showStartTime: boolean;
  readonly showEndTime: boolean;
  readonly hintModalData?: any;
  readonly basicFields?: any[];
};

export class DefaultCreativeBasicFormModel implements CreativeBasicFormModel {
  type: string;
  event: FireableUpdateEventListener<CreativeBasicFormModel>;
  loading: boolean;
  initCreative: CreativeFormBasicData;
  contentModel?: FormContentModel;
  tenmaxCategories: Array<SelectOptions>;
  creativeManager: CreativeManager;
  showStartTime: boolean;
  showEndTime: boolean;
  hintModalData?: any;
  urlMacroHintModalData?: any;
  basicFields: any = [];
  formikProps?: FormikProps<CreativeFormBasicData>;
  currentCreativeType: CreativeType;

  constructor(
    type: string,
    creative: CreativeFormBasicData,
    tenmaxCategories: Array<SelectOptions>,
    creativeManager: CreativeManager,
    public enabledCreativeTypes: number[],
    private onTypeChange: (creativeType: number) => void,
    private formContentModelGetter: (
      creativeType: number
    ) => Promise<FormContentModel>,
    private limitationHook: (
      operate: string,
      limitationType: string,
      label: string,
      value: string
    ) => void,
    private forPmp: boolean = false
  ) {
    this.type = type;
    this.event = new FireableUpdateEventListener<CreativeBasicFormModel>();
    this.loading = false;
    this.initCreative = creative;
    this.showStartTime = !!creative.enableStartTime;
    this.showEndTime = !!creative.enableEndTime;
    this.formContentModelGetter(creative.creativeType).then((contentModel) => {
      this.contentModel = contentModel;
      this.contentModel.setLimitationHook(this.limitationHook);
      this.updateBasicFields();
      this.updateState(false);
    });
    this.tenmaxCategories = tenmaxCategories;
    this.creativeManager = creativeManager;
    this.currentCreativeType = creative.creativeType;
    this.updateBasicFields();
  }

  get needLandingUrl() {
    return this.contentModel ? this.contentModel.needBannerUrl : true;
  }

  get state(): CreativeBasicFormState {
    return {
      loading: this.loading,
      needLandingUrl: this.needLandingUrl,
      showStartTime: this.showStartTime,
      showEndTime: this.showEndTime,
      hintModalData: this.hintModalData,
      basicFields: this.basicFields
    };
  }

  updateBasicFields() {
    const enableSelectCreativeTypeModelTypes = ['create', 'createAndBind'];
    // contentModel 是非同步載入的，第一次跑到這裡時還是 undefined（欄位先顯示），
    // 載完會再呼叫一次 updateBasicFields 把該藏的藏掉。
    const hideEnableTime =
      this.forPmp || !!this.contentModel?.hideCreativeEnableTime;
    this.basicFields = new FormConfig.FieldsBuilder()
      .addFormikSelect(
        {
          label: i18n.t('creativeSetupFlow.labels.creativeType'),
          name: 'creativeType',
          simpleValue: true,
          options: this.enabledCreativeTypes.map((type) => {
            const label =
              this.creativeManager.getCreativeTypeSetting(type).name;
            return {
              label,
              value: type,
              // 渠道狀態當場從 registry 讀，不要用 creativeManager 那份在建構子拍好的
              // 快照 —— 它只保證 channelGroupId 正確，screenProperty 會是舊的。
              disabled: isCompositeSettingBlocked(type)
            };
          }),
          optionComponent: CompositeSettingOption,
          onChange: this.onCreativeTypeChange,
          validate: this.validateCreativeType
        },
        !enableSelectCreativeTypeModelTypes.includes(this.type)
      )
      .addFormikLabel(
        {
          label: i18n.t('creativeSetupFlow.labels.creativeType'),
          name: 'creativeType',
          formatter: (value) =>
            this.creativeManager.getCreativeTypeLabel(+value)
        },
        enableSelectCreativeTypeModelTypes.includes(this.type)
      )
      .addFormikSelect({
        label: i18n.t('creativeSetupFlow.labels.tenmaxCategory'),
        name: 'tenmaxCategory',
        simpleValue: true,
        options: this.tenmaxCategories.map((category) => {
          return {
            value: category.value,
            label: i18n.t(`tenmaxCategory.labels.${category.label}`)
          };
        }),
        validate: validateEmpty
      })
      .addFormikInput({
        label: i18n.t('creativeSetupFlow.labels.name'),
        name: 'name',
        validate: validateEmpty
      })
      .addFormikUrlInput(
        {
          label: i18n.t('creativeSetupFlow.labels.bannerUrl'),
          name: 'bannerUrl',
          className: styles.bannerUrl,
          validate: this.validateBannerUrl
        },
        !this.needLandingUrl
      )
      .addFormikCustom(
        {
          label: i18n.t('creativeSetupFlow.labels.enableStartTime'),
          name: 'enableStartTime',
          render: _.partial(
            renderStartTime,
            this.showStartTime,
            this.setShowStartTime.bind(this)
          )
        },
        hideEnableTime
      )
      .addFormikCustom(
        {
          label: i18n.t('creativeSetupFlow.labels.enableEndTime'),
          name: 'enableEndTime',
          render: _.partial(
            renderEndTime,
            this.showEndTime,
            this.setShowEndTime.bind(this)
          )
        },
        hideEnableTime
      )
      .build();
  }

  getInitFormConfig = () => {
    return new FormConfig.Builder()
      .addSection(
        new FormConfig.SectionBuilder(this.basicFields)
          .withTitle(i18n.t('creativeSetupFlow.labels.creativeBasicInfo'))
          .build()
      )
      .build();
  };

  getContentModelByCreativeType = () => {
    return this.contentModel;
  };

  setShowStartTime = (showStartTime: boolean): void => {
    this.showStartTime = showStartTime;
    this.updateBasicFields();
    this.updateState(false);
  };

  setShowEndTime = (showEndTime: boolean): void => {
    this.showEndTime = showEndTime;
    this.updateBasicFields();
    this.updateState(false);
  };

  setLimitationHook(
    hook: (
      operate: string,
      limitationType: string,
      label: string,
      value: string
    ) => void
  ): void {
    this.limitationHook = hook;
    this.contentModel &&
      this.contentModel.setLimitationHook(this.limitationHook);
  }

  setFormikProps(props: FormikProps<any>) {
    this.formikProps = props;
  }

  getCreativeBasicValue() {
    if (!this.formikProps) {
      return {};
    }
    return this.formikProps.values;
  }

  /**
   * ECOPD-5722：把選項設成 disabled 只擋得住「選它」，擋不住「它已經是值」——
   * 從連台廣告活動底下建立素材時，那個活動的素材類型就只有一個，一進畫面就已經選中。
   * 沒有這一道，使用者會填完整張表、上傳完檔案，送出才被後端擋。
   */
  validateCreativeType = (creativeType) => {
    const emptyError = validateEmpty(creativeType);
    if (emptyError) {
      return emptyError;
    }
    return isCompositeSettingBlocked(creativeType)
      ? getCompositeSettingInvalidHint()
      : undefined;
  };

  validateBannerUrl = (url) => {
    if (this.loading) {
      return i18n.t('creativeSetupFlow.labels.analyzingHint');
    }

    return validateUrl(url);
  };

  validate = async () => {
    if (!this.formikProps) {
      return {};
    }

    const errors = await this.formikProps.validateForm();
    return errors;
  };

  onCreativeTypeChange = async (creativeType) => {
    this.contentModel = await this.formContentModelGetter(creativeType);
    this.contentModel.setLimitationHook(this.limitationHook);
    this.showStartTime = false;
    this.showEndTime = false;
    if (this.formikProps && this.contentModel) {
      const resetValue = {
        ...this.formikProps.values,
        creativeType,
        typeProperties: this.contentModel.getInitTypeProperties(),
        medias: {},
        enableNativeBanner: isNative(creativeType) ? true : undefined,
        enableStartTime: undefined,
        enableEndTime: undefined
      };
      const enableAdLogo = this.contentModel.isAdLogoSupported(resetValue);
      if (enableAdLogo) {
        resetValue.adLogo = this.contentModel.defaultAdLogo;
      }
      this.formikProps.resetForm({ values: resetValue, errors: {} });
    }
    this.urlMacroHintModalData = undefined;
    this.hintModalData = undefined;
    this.currentCreativeType = creativeType;
    this.updateBasicFields();
    this.onTypeChange(creativeType);
  };

  setHintModalData = (data?: any) => {
    this.hintModalData = data;
    if (data) {
      this.urlMacroHintModalData = undefined;
    }
    this.updateState(false);
  };

  triggerUrlMacroHint = () => {
    if (this.urlMacroHintModalData) {
      this.urlMacroHintModalData = undefined;
    } else {
      this.urlMacroHintModalData = {
        component: UrlMacroInstruction,
        props: {
          onClose: _.partial(this.setHintModalData, undefined)
        }
      };
    }
    this.hintModalData = this.urlMacroHintModalData;
    this.updateState(false);
  };

  onUnmount(handler?: number) {
    handler && this.event.remove(handler);
    if (this.formikProps) {
      this.initCreative = this.formikProps?.values;
    }
  }

  updateState(loading: boolean) {
    this.loading = loading;
    this.event.fireEvent(this);
  }
}
